import deepIterator from "deep-iterator";
import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useIsMounted } from "usehooks-ts";
import { appParamState } from "./atoms";
import BCFMain from "./bcf/components/BCFMain";
import BookingDotComMain from "./bookingdotcom/components/BookingDotComMain";
import LoadingBlankOverlay from "./common/components/LoadingBlankOverlay";
import { EventLogger } from "./common/utils/EventLogger";
import Comp1000PrizesMain from "./comp1000prizes/components/Comp1000PrizesMain";
import { FLOW, GLOBAL_CONST, REWARD_OFFERING } from "./constants";
import EuropcarMain from "./europcar/components/EuropcarMain";
import GlobalEscapesMain from "./globalescapes/components/GlobalEscapesMain";
import MyCarMain from "./mycar/components/MyCarMain";
import MyCarTacticalMain from "./mycar/components/MyCarTacticalMain";
import OriginEnergyMain from "./origin-energy/components/OriginEnergyMain";
import SecureParkingMain from "./secureparking/components/SecureParkingMain";
import ShellMain from "./shell/components/ShellMain";
import ShellFuelUpMain from "./shellfuelup/components/ShellFuelUpMain";
import StarCarWashMain from "./starcarwash/components/StarCarWashMain";
import { cacheImage, cacheLottie, fromPublic, globalErrorHandler, redirectError } from "./utils";
import CompWinChooseFlyMain from "./compwinchoosefly/components/CompWinChooseFlyMain";
import SuperCheapAutoMain from "./supercheapauto/components/SuperCheapAutoMain";
import SuperCheapAutoTactical1Main from "./supercheapauto/components/SuperCheapAutoTactical1Main";
import SuperCheapAutoTactical2Main from "./supercheapauto/components/SuperCheapAutoTactical2Main";
import SuperCheapAutoTactical3Main from "./supercheapauto/components/SuperCheapAutoTactical3Main";
import EuropcarTacticalMain from "./europcar/components/EuropcarTacticalMain";
import EuropcarTactical2Main from "./europcar/components/EuropcarTactical2Main";
import EuropcarTactical3Main from "./europcar/components/EuropcarTactical3Main";
import SydneyAirportMain from "./sydneyairport/components/SydneyAirportMain";

import TotalToolsMain from "./totaltools/components/TotalToolsMain";
import TourDeBrisbaneMain from "./tourdebrisbane/components/TourDeBrisbaneMain";
import RunForKidsMain from "./runforkids/components/RunForKidsMain";
import StarCarWashTacticalMain from "./starcarwash/components/StarCarWashTacticalMain";
import StarCarWashTactical2Main from "./starcarwash/components/StarCarWashTactical2Main";
import SydneyAirportTacticalMain from "./sydneyairport/components/SydneyAirportTacticalMain";
import SydneyMarathonMain from "./sydneymarathon/components/SydneyMarathonMain";
import BridgeToBrisbaneMain from "./bridgetobrisbane/BridgeToBrisbaneMain";
import WeekOneMain from "./weeklycompetition/WeekOneMain";
import WeekTwoMain from "./weeklycompetition/WeekTwoMain";
import WeekFourMain from "./weeklycompetition/WeekFourMain";
import WeekThreeMain from "./weeklycompetition/WeekThreeMain";
import ComingSoonFuelMain from "./comingsoonfuel/components/ComingSoonnFuel";
import SevenElevenMain from "./7eleven/components/7ElevenMain";
import SevenElevenTacticalMain from "./7eleven/components/7ElevenTacticalMain";
import BridgeToBrisbaneTacticalMain from "./bridgetobrisbane/BridgeToBrisbaneTacticalMain";
import SummerGiveawayExtraMain from "./summergiveawayextra/components/SummerGiveawayExtraMain";
import WgtExtraMain from "./wgtextra/components/WgtExtraMain";
import WgtBehaviourMain from "./wgtbehaviour/components/WgtBehaviourMain";
import CareParkMain from "./carepark/compoonents/CareParkMain";
import EasterGiveawayExtraMain from "./eastergiveawayextra/components/EasterGiveawayExtraMain";
import TripComCompetitionMain from "./tripcomcompetition/components/TripComCompetitionMain";
import TripComMain from "./tripcom/components/TripComMain";
import SydneyMarathon42kmMain from "./sydneymarathon42km/components/SydneyMarathon42kmMain";
import SydneyMarathon5kmMain from "./sydneymarathon5km/components/SydneyMarathon5kmMain";



function App() {
  const [appParam, setAppParam] = useRecoilState(appParamState);
  const isMounted = useIsMounted();

  useEffect(() => {
    EventLogger.logInfoEvent("Rewards page launched", {
      // eslint-disable-next-line no-restricted-globals
      queryParams: location.search,
      event: "PageLaunched",
      rewardId: GLOBAL_CONST.REWARDS_ID,
    });
  });

  // Determine the view height so carousel slides can be set correctly. Mostly for Safari on mobile.
  useEffect(() => {
    // Unfortunately because the contents are injected into the SPA, we still need to rely on good old scripting.
    // eslint-disable-next-line no-restricted-globals
    const queryParams = new URLSearchParams(location.search);
    const flow = (queryParams.get("flow") as FLOW) || GLOBAL_CONST.FLOW;

    // Register global error handler.
    window.onerror = globalErrorHandler;

    // Preload all images to give a better webview (mimic native) experience.
    async function preloadAssets() {
      try {
        const currentRewardCfg = GLOBAL_CFG[GLOBAL_CONST.REWARDS_ID];

        // Get a list of images to preload from the global copy config, this should be most images.
        const imagesToPreload = Array.from(deepIterator(currentRewardCfg, { onlyLeaves: true }))
          .map((e: any) => e._value)
          .filter((e: any) => typeof e === 'string' && (e.endsWith(".svg") || e.endsWith(".png") || e.endsWith(".jpg") || e.endsWith(".jpeg")))
          .map((src: any) => fromPublic(src));

        await Promise.all(imagesToPreload.map((src: string) => cacheImage(src)));

        // Preload lottie animations where applicable
        let lottiesToPreload = Array.from(deepIterator(currentRewardCfg, { onlyLeaves: true }))
          .map((e: any) => e._value)
          .filter((e: any) => typeof e === 'string' && e.endsWith(".json"))
          .map((src: any) => fromPublic(src));

        // Ensure party.json is always preloaded
        const partyLottiePath = fromPublic("/custom/common/party.json");
        if (!lottiesToPreload.includes(partyLottiePath)) {
          lottiesToPreload.push(partyLottiePath);
        }
        // Remove duplicates that might have been added if partyLottiePath was already found by deepIterator
        lottiesToPreload = Array.from(new Set(lottiesToPreload));


        await Promise.all(lottiesToPreload.map((url: string) => cacheLottie(url)));

        // Store params into atom store.
        setAppParam({ flow });
      } catch (e) {
        console.error("Fatal error occurred preloading assets.", e);
        await EventLogger.logErrorEvent("Fatal error occurred preloading assets.", e);
        redirectError();
      }
    }

    preloadAssets();
  }, [isMounted, setAppParam]);

  // set the reward offering id as defined in the last path segment.
  const rewardIdStr = useLocation().pathname.split("/").pop();
  GLOBAL_CONST.REWARDS_ID = rewardIdStr === "" ? GLOBAL_CONST.REWARDS_ID : (rewardIdStr as REWARD_OFFERING);

  return (
    <>
      <LoadingBlankOverlay />
      {/* Important not to load any child component when there are pending resources loading */}
      {appParam.flow === FLOW.PENDING ? (
        <></>
      ) : (
        <Routes>
          {/* Default to Shell */}
          <Route path="/" element={<ShellMain />} />
          <Route path="/7-eleven" element={<SevenElevenMain/>} />
          <Route path="/tactical-7-eleven" element={<SevenElevenTacticalMain/>} />
          <Route path="/bcf" element={<BCFMain/>} />
          <Route path="/europcar" element={<EuropcarMain />} />
          <Route path="/europcar-tactical" element={<EuropcarTacticalMain />} />
          <Route path="/europcar-tactical-2" element={<EuropcarTactical2Main />} />
          <Route path="/europcar-tactical-3" element={<EuropcarTactical3Main />} />
          <Route path="/rewards-shell-express" element={<ShellMain />} />
          <Route path="/fuel-up-your-savings" element={<ShellFuelUpMain />} />
          <Route path="/star-car-wash" element={<StarCarWashMain />} />
          <Route path="/star-car-wash-tactical" element={<StarCarWashTacticalMain />} />
          <Route path="/star-car-wash-tactical-2" element={<StarCarWashTactical2Main />} />
          <Route path="/booking-dot-com" element={<BookingDotComMain />} />
          <Route path="/comp-1000-prizes" element={<Comp1000PrizesMain />} />
          <Route path="/comp-winchoosefly" element={<CompWinChooseFlyMain />} />
          <Route path="/secure-parking" element={<SecureParkingMain />} />
          <Route path="/mycar" element={<MyCarMain />} />
          <Route path="/mycar-tactical" element={<MyCarTacticalMain />} />
          <Route path="/origin-energy" element={<OriginEnergyMain />} />
          <Route path="/global-escapes" element={<GlobalEscapesMain />} />
          <Route path="/run-for-kids" element={<RunForKidsMain />} />
          <Route path="/supercheap-auto" element={<SuperCheapAutoMain />} />
          <Route path="/supercheap-auto-tactical-1" element={<SuperCheapAutoTactical1Main />} />
          <Route path="/supercheap-auto-tactical-2" element={<SuperCheapAutoTactical2Main />} />
          <Route path="/supercheap-auto-tactical-3" element={<SuperCheapAutoTactical3Main />} />
          <Route path="/total-tools" element={<TotalToolsMain />} />
          <Route path="/sydney-airport" element={<SydneyAirportMain />} />
          <Route path="/sydney-airport-tactical" element={<SydneyAirportTacticalMain />} />
          <Route path="/sydney-marathon" element={<SydneyMarathonMain/>} />
          <Route path="/tour-de-brisbane" element={<TourDeBrisbaneMain />} />
          <Route path="/care-park" element={<CareParkMain />} />
          <Route path="/week-one" element={<WeekOneMain/>} />
          <Route path="/week-two" element={<WeekTwoMain/>} />
          <Route path="/week-three" element={<WeekThreeMain/>} />
          <Route path="/week-four" element={<WeekFourMain/>} />
          <Route path="/bridge-to-brisbane" element={<BridgeToBrisbaneMain/>} />
          <Route path="/bridge-to-brisbane-tactical" element={<BridgeToBrisbaneTacticalMain/>} />
          <Route path="/wgt-extra" element={<WgtExtraMain/>} />
          <Route path="/wgt-1" element={<WgtBehaviourMain offerId={REWARD_OFFERING.wgt1} />} />
          <Route path="/wgt-2" element={<WgtBehaviourMain offerId={REWARD_OFFERING.wgt2} />} />
          <Route path="/wgt-3" element={<WgtBehaviourMain offerId={REWARD_OFFERING.wgt3} />} />
          <Route path="/wgt-4" element={<WgtBehaviourMain offerId={REWARD_OFFERING.wgt4} />} />
          <Route path="/coming-soon-fuel" element={<ComingSoonFuelMain/>} />
          <Route path="/summerGiveawayExtra" element={<SummerGiveawayExtraMain/>} />
          <Route path="/easter-giveaway-extra" element={<EasterGiveawayExtraMain/>} />
          <Route path="/trip-com-competition" element={<TripComCompetitionMain/>} />
          <Route path="/trip-com" element={<TripComMain/>} />
          <Route path="/sydney-marathon-42km" element={<SydneyMarathon42kmMain/>} />
          <Route path="/sydney-marathon-5km" element={<SydneyMarathon5kmMain/>} />
        </Routes>
      )}
    </>
  );
}

export default App;